import { useState, useCallback } from "react";
import { Search, Loader2, SlidersHorizontal, User, MapPin, BadgeCheck } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PartnerCard } from "@/components/PartnerCard";
import { SPORTS, SPORT_ICONS } from "@/data/sports";
import { AVAILABILITY_OPTIONS, FILTER_MODES } from "@/data/matching";
import api, { formatApiError } from "@/lib/api";

const ANY = "any";

export function FindPartnerDialog({ open, onOpenChange, onRequested, hasActivePartner, pendingIds = [] }) {
  const [filters, setFilters] = useState({ sport: ANY, country: "", state: "", city: "", availability: ANY, mode: ANY });
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [requesting, setRequesting] = useState(null);
  const [localRequested, setLocalRequested] = useState([]);

  const search = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.sport !== ANY) params.sport = filters.sport;
      if (filters.country.trim()) params.country = filters.country.trim();
      if (filters.state.trim()) params.state = filters.state.trim();
      if (filters.city.trim()) params.city = filters.city.trim();
      if (filters.availability !== ANY) params.availability = filters.availability;
      if (filters.mode !== ANY) params.mode = filters.mode;
      const { data } = await api.get("/partners/discover", { params });
      setResults(data.partners);
      setSearched(true);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const sendRequest = async (partner) => {
    if (hasActivePartner) {
      toast.error("You already have an active partner. End that relationship first.");
      return;
    }
    setRequesting(partner.id);
    try {
      await api.post("/matches/request", { partner_id: partner.id });
      setLocalRequested((r) => [...r, partner.id]);
      toast.success(`Match request sent to ${partner.display_name}!`);
      onRequested?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setRequesting(null);
    }
  };

  const requestedSet = new Set([...pendingIds, ...localRequested]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0A0A0B] border-zinc-800 max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="find-partner-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-black uppercase tracking-tight flex items-center gap-2">
            <Search className="w-5 h-5 text-[#E53E3E]" /> Find a Partner
          </DialogTitle>
          <DialogDescription className="text-zinc-500">Browse approved accountability partners and send a match request.</DialogDescription>
        </DialogHeader>

        {/* Filters */}
        <div className="rounded-xl border border-zinc-800 bg-zinc-900/60 p-4">
          <p className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-3 flex items-center gap-2"><SlidersHorizontal className="w-3.5 h-3.5" /> Filters</p>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
            <Select value={filters.sport} onValueChange={(v) => setFilters({ ...filters, sport: v })}>
              <SelectTrigger className="bg-zinc-900 border-zinc-800 h-11" data-testid="filter-sport"><SelectValue placeholder="Sport" /></SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">
                <SelectItem value={ANY}>Any sport</SelectItem>
                {SPORTS.map((s) => <SelectItem key={s.slug} value={s.slug}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filters.mode} onValueChange={(v) => setFilters({ ...filters, mode: v })}>
              <SelectTrigger className="bg-zinc-900 border-zinc-800 h-11" data-testid="filter-mode"><SelectValue placeholder="Mode" /></SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">
                <SelectItem value={ANY}>Any mode</SelectItem>
                {FILTER_MODES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filters.availability} onValueChange={(v) => setFilters({ ...filters, availability: v })}>
              <SelectTrigger className="bg-zinc-900 border-zinc-800 h-11" data-testid="filter-availability"><SelectValue placeholder="Availability" /></SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">
                <SelectItem value={ANY}>Any availability</SelectItem>
                {AVAILABILITY_OPTIONS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input value={filters.country} onChange={(e) => setFilters({ ...filters, country: e.target.value })} placeholder="Country" className="h-11 bg-zinc-900 border-zinc-800" data-testid="filter-country" />
            <Input value={filters.state} onChange={(e) => setFilters({ ...filters, state: e.target.value })} placeholder="State / Region" className="h-11 bg-zinc-900 border-zinc-800" data-testid="filter-state" />
            <Input value={filters.city} onChange={(e) => setFilters({ ...filters, city: e.target.value })} placeholder="City" className="h-11 bg-zinc-900 border-zinc-800" data-testid="filter-city" />
          </div>
          <Button onClick={search} disabled={loading} className="mt-4 bg-[#E53E3E] hover:bg-[#C53030] rounded-full font-semibold h-11 px-6" data-testid="find-partner-search">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Search className="w-4 h-4 mr-2" /> Search Partners</>}
          </Button>
        </div>

        {hasActivePartner && (
          <p className="text-sm text-amber-400 bg-amber-400/10 rounded-lg px-4 py-2">You already have an active partner. End that relationship before requesting another.</p>
        )}

        {/* Results */}
        <div className="mt-2">
          {loading ? (
            <div className="py-12 grid place-items-center"><Loader2 className="w-7 h-7 text-[#E53E3E] animate-spin" /></div>
          ) : searched && results.length === 0 ? (
            <p className="py-12 text-center text-zinc-500" data-testid="find-partner-empty">No approved partners match your filters yet. Try widening your search.</p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="find-partner-results">
              {results.map((p) => (
                <PartnerCard key={p.id} partner={p} onView={setViewing} onRequest={sendRequest}
                  requested={requestedSet.has(p.id) || requesting === p.id} disabled={hasActivePartner} />
              ))}
            </div>
          )}
        </div>
      </DialogContent>

      {/* View profile */}
      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="bg-[#0A0A0B] border-zinc-800 max-w-lg" data-testid="partner-profile-dialog">
          {viewing && (
            <div>
              <DialogTitle className="sr-only">Partner profile</DialogTitle>
              <DialogDescription className="sr-only">Approved accountability partner details.</DialogDescription>
              <div className="flex items-center gap-4">
                <div className="w-20 h-20 rounded-full overflow-hidden border border-zinc-700 bg-zinc-800 grid place-items-center shrink-0">
                  {viewing.photo ? <img src={viewing.photo} alt={viewing.display_name} className="w-full h-full object-cover" /> : <User className="w-8 h-8 text-zinc-500" />}
                </div>
                <div>
                  <div className="flex items-center gap-1.5">
                    <h3 className="font-display text-2xl font-black uppercase tracking-tight">{viewing.display_name}</h3>
                    {viewing.verified && <BadgeCheck className="w-5 h-5 text-[#E53E3E]" />}
                  </div>
                  <p className="text-sm text-zinc-400 flex items-center gap-1 mt-1"><MapPin className="w-3.5 h-3.5" /> {[viewing.city, viewing.state, viewing.country].filter(Boolean).join(", ") || "Location not set"}</p>
                </div>
              </div>
              {viewing.bio && <p className="mt-4 text-sm text-zinc-300 leading-relaxed">{viewing.bio}</p>}
              <div className="mt-4 grid gap-3 text-sm">
                <div><span className="text-xs uppercase tracking-widest text-zinc-500">Sports</span><div className="mt-1.5 flex flex-wrap gap-1.5">{(viewing.sports || []).map((s) => <span key={s} className="px-2 py-1 rounded-full text-xs bg-zinc-800 text-zinc-300 capitalize">{s.replace("-", " ")}</span>)}</div></div>
                <div><span className="text-xs uppercase tracking-widest text-zinc-500">Availability</span><p className="mt-1 text-zinc-300">{(viewing.availability || []).join(", ") || "—"}</p></div>
                <div><span className="text-xs uppercase tracking-widest text-zinc-500">Accountability Mode</span><p className="mt-1 text-zinc-300">{viewing.accountability_mode || "—"}</p></div>
              </div>
              <Button onClick={() => { sendRequest(viewing); setViewing(null); }} disabled={hasActivePartner || requestedSet.has(viewing.id)} className="mt-6 w-full bg-[#E53E3E] hover:bg-[#C53030] rounded-full font-semibold h-11" data-testid="partner-profile-request">
                {requestedSet.has(viewing.id) ? "Request Sent" : "Send Match Request"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
